import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox
} from "@mui/material";

import MuiAlert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { SERVER_URL } from "../../constants.js";
import AddEditProductDialog from "./AddEditProductDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { jsonOr } from "../../utils/http";
import {
  CSV_SENTINEL_HEADER,
  CSV_SENTINEL_VALUE,
  parseCsv,
  normalizeHeader,
  downloadCsv,
} from "../shared utils/csv";

const TAB = {
    ALL: 0,
    BOOKED: 1,
    SOLD_OUT: 2,
};

// Keep this “friendly” list, but allow free-text fallback in the dialog if needed.
const PRODUCT_TYPE_OPTIONS = [
    "RESIDENTIAL",
    "AFTERNOON",
    "EXTERNAL",
    "WEEKEND",
    "EXTRA",
    "EVENT",
];

function displayProductType(type) {
    if (String(type || "").toUpperCase() === "RESIDENTIAL") return "BOOKING";
    return type || "—";
}

// A RESIDENTIAL product is labelled "BOOKING" everywhere on screen, so a CSV
// filled in from what the user can see says BOOKING. Accept either spelling,
// case-insensitively, and store the canonical value the backend expects.
function canonicalProductType(raw) {
    const s = String(raw || "").trim().toUpperCase();
    return s === "BOOKING" ? "RESIDENTIAL" : s;
}

// BOOKING products carry a separate residential Yes/No flag; the rest are
// always residential (same rule the add/edit dialog applies on save).
function isBookingType(productType) {
    return String(productType || "").toUpperCase() === "RESIDENTIAL";
}

function TabPanel({ value, index, children }) {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

// Parser, sentinel and download live in shared utils/csv.js (also used by the
// booking-group student upload).

const CSV_HEADERS = [
  CSV_SENTINEL_HEADER,   // sentinel for template/example row detection
  "name",
  "base",
  "productType",
  "residential",         // BOOKING rows only; blank means Yes
  "startDate",
  "endDate",
  "defaultRate",
  "capacity",
  "commissionable",
  "notes",
];

function asBool(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return false;
  return ["true", "yes", "y", "1"].includes(s);
}

// A Yes/No that may also be left blank. Returns true/false, null when blank so
// the caller can apply its own default, or undefined when the value isn't
// recognised — a typo must not quietly read as "No".
function asOptionalBool(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  if (["true", "yes", "y", "1"].includes(s)) return true;
  if (["false", "no", "n", "0"].includes(s)) return false;
  return undefined;
}

function asNumberOrNull(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function isIsoDate(v) {
  // simple YYYY-MM-DD check; dayjs will validate deeper if needed
  return /^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim());
}

function buildTemplateCsv() {
  // Two examples rather than one, so a BOOKING row and its residential flag are
  // shown by example. Both carry the sentinel and are skipped on import.
  const examples = [
    {
      [CSV_SENTINEL_HEADER]: CSV_SENTINEL_VALUE,
      name: "Example product name",
      base: "Bathgate",
      productType: "AFTERNOON",
      residential: "",
      startDate: "2026-07-06",
      endDate: "2026-07-10",
      defaultRate: "120.00",
      capacity: "30",
      commissionable: "Yes",
      notes: "This is an example row. Leave it in; it will be skipped on import.",
    },
    {
      [CSV_SENTINEL_HEADER]: CSV_SENTINEL_VALUE,
      name: "Example non-residential booking",
      base: "Bathgate",
      productType: "BOOKING",
      residential: "No",
      startDate: "2026-07-06",
      endDate: "2026-07-13",
      defaultRate: "600.00",
      capacity: "20",
      commissionable: "Yes",
      notes: "residential is for BOOKING rows only. Leave it blank for Yes.",
    },
  ];

  const line = (row) =>
    CSV_HEADERS.map((h) => `"${String(row[h] ?? "").replaceAll('"', '""')}"`).join(",");

  return [CSV_HEADERS.join(","), ...examples.map(line)].join("\n") + "\n";
}

export default function ProductsPage({ selectedYear }) {
    
    const [tab, setTab] = useState(TAB.ALL);
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [campuses, setCampuses] = useState([]);
    const [campusSessions, setCampusSessions] = useState([]);

    // Filters
    const [nameFilter, setNameFilter] = useState("");
    const [baseFilter, setBaseFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    
    // Date range filter (inclusive)
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;
    const [rangeStart, setRangeStart] = useState(yearStart);
    const [rangeEnd, setRangeEnd] = useState(yearEnd);
    const [rangeSwapped, setRangeSwapped] = useState(false);
  
    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    // Toast
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");

    //Dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingProduct, setDeletingProduct] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Bulk upload dialog state
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFileName, setBulkFileName] = useState("");
    const [bulkParsing, setBulkParsing] = useState(false);
    const [bulkRows, setBulkRows] = useState([]); // normalized objects
    const [bulkErrors, setBulkErrors] = useState([]); // strings
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
    // Multi-select delete
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    useEffect(() => {
        setRangeStart(`${selectedYear}-01-01`);
        setRangeEnd(`${selectedYear}-12-31`);
    }, [selectedYear]);

    useEffect(() => {
        const token = sessionStorage.getItem("bearer");
        fetch(`${SERVER_URL}api/campuses`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => (jsonOr(res, [])))
        .then((data) => {
            const list = Array.isArray(data) ? data : [];
            setCampusSessions(list);
            // /api/campuses returns one row per camp session, so the same
            // campus name appears multiple times. Products are keyed by campus
            // name (string), so we dedupe to one entry per unique name for
            // the base dropdown.
            const byName = new Map();
            list.forEach((c) => {
                const key = String(c.name || "").trim();
                if (!key) return;
                if (!byName.has(key)) byName.set(key, { ...c, name: key });
            });
            const deduped = Array.from(byName.values()).sort((a, b) =>
                (a.name || "").localeCompare(b.name || "")
            );
            setCampuses(deduped);
        })
        .catch((err) => console.error("Failed to fetch campuses:", err));
    }, []);

    useEffect(() => {
        // clear selections when products set changes
        setSelectedIds(new Set());
    }, [products]);

    const normalizeRange = () => {
        const start = rangeStart;
        const end = rangeEnd;

        if (!start || !end) return;
        if (!dayjs(start).isValid() || !dayjs(end).isValid()) return;

        if (dayjs(start).isAfter(dayjs(end), "day")) {
            setRangeStart(end);
            setRangeEnd(start);

            setRangeSwapped(true);
            setTimeout(() => setRangeSwapped(false), 900);

            showToast("Date range swapped (start was after end).", "info");
        }
    };

    const showToast = (msg, severity = "success") => {
        setToastMessage(msg);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    const endpointForTab = (t) => {
        if (t === TAB.BOOKED) return "api/products/booked";
        if (t === TAB.SOLD_OUT) return "api/products/soldOut";
        return "api/products";
    };

    const fetchProducts = async () => {
        const token = sessionStorage.getItem("bearer");
        setLoading(true);

        try {
            // Always include start/end (page is designed around a window)
            const qs = new URLSearchParams({
                start: rangeStart,
                end: rangeEnd,
            });

            const url = `${SERVER_URL}${endpointForTab(tab)}?${qs.toString()}`;

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.status === 204) {
                setProducts([]);
                return;
            }

            if (!res.ok) throw new Error(`Fetch failed (${res.status})`);

            const data = await res.json();
            setProducts(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch products:", e);
            setProducts([]);
            showToast("Failed to load products.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRangeStart(`${selectedYear}-01-01`);
        setRangeEnd(`${selectedYear}-12-31`);
    }, [selectedYear]);

    useEffect(() => {
        if (!rangeStart || !rangeEnd) return;
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, rangeStart, rangeEnd]);

    const filtered = useMemo(() => {
        return (products || []).filter((p) => {
            const matchesName =
                !nameFilter ||
                (p.name || "").toLowerCase().includes(nameFilter.toLowerCase());

            const matchesBase =
                !baseFilter ||
                (p.base || "").toLowerCase().includes(baseFilter.toLowerCase());

            const matchesType =
                !typeFilter || String(p.productType || "") === String(typeFilter);

            return matchesName && matchesBase && matchesType;
        });
    }, [products, nameFilter, baseFilter, typeFilter]);

    const openAdd = () => {
        setEditingProduct(null);
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditingProduct(p);
        setDialogOpen(true);
    };

    const openClone = (p) => {
        // Prime the dialog with the source product as a template:
        // drop the id so save creates a new row, and clear the allocated
        // counter so the new product starts fresh.
        const { id, allocated, ...rest } = p || {};
        setEditingProduct({
            ...rest,
            name: rest.name ? `${rest.name} (copy)` : "",
            allocated: 0,
        });
        setDialogOpen(true);
    };

    const handleToastClose = (event, reason) => {
        if (reason === "clickaway") return;
        setToastOpen(false);
    };

    const handleSave = async (payload) => {
        const token = sessionStorage.getItem("bearer");

        const isEdit = !!payload?.id;
        const url = isEdit
            ? `${SERVER_URL}api/products/${payload.id}`
            : `${SERVER_URL}api/products`;

        const method = isEdit ? "PUT" : "POST";

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                console.error("Save failed:", res.status, text);
                showToast("Failed to save product.", "error");
                return false;
            }

            showToast(isEdit ? "Product updated." : "Product created.", "success");
            setDialogOpen(false);
            await fetchProducts();
            return true;
        } catch (e) {
            console.error("Network error saving product:", e);
            showToast("Network error saving product.", "error");
            return false;
        }
    };

    const canDeleteProduct = (p) => {
        // Using allocated as the booking count proxy
        // If allocated > 0, it has bookings and should not be deletable
        return !p?.allocated || Number(p.allocated) === 0;
    };

    const openDelete = (p) => {
        if (!canDeleteProduct(p)) {
            showToast(
                "Cannot delete this product because it has bookings. You need to cancel bookings first.",
                "warning"
            );
            return;
        }
        setDeletingProduct(p);
        setDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!deletingProduct) return;

        const token = sessionStorage.getItem("bearer");
        setDeleteLoading(true);

        try {
            const res = await fetch(`${SERVER_URL}api/products/${deletingProduct.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.ok) {
                showToast("Product deleted.", "success");
                setDeleteOpen(false);
                setDeletingProduct(null);
                await fetchProducts();
                return;
            }

            // Try to read any backend message (optional)
            const text = await res.text().catch(() => "");

            // Common case: product has bookings / FK constraint
            if (res.status === 409 || res.status === 400 || res.status === 500) {
                showToast(
                    "Cannot delete this product because it has bookings. You need to cancel bookings first.",
                    "warning"
                );
            } else {
                console.error("Delete failed:", res.status, text);
                showToast("Failed to delete product.", "error");
            }
        } catch (e) {
            console.error("Network error deleting product:", e);
            showToast("Network error deleting product.", "error");
         } finally {
            setDeleteLoading(false);
        }
    };

    const closeDelete = () => {
        if (deleteLoading) return;
        setDeleteOpen(false);
        setDeletingProduct(null);
    };

    const resetBulk = () => {
        setBulkFileName("");
        setBulkRows([]);
        setBulkErrors([]);
        setBulkParsing(false);
        setBulkImporting(false);
        setBulkProgress({ done: 0, total: 0 });
    };

    const openBulk = () => {
        resetBulk();
        setBulkOpen(true);
    };

    const closeBulk = () => {
        if (bulkParsing || bulkImporting) return;
        setBulkOpen(false);
    };

    function pad2(n) {
      return String(n).padStart(2, "0");
    }

    // Parses:
    // - YYYY-MM-DD (preferred)
    // - DD/MM/YYYY or D/M/YYYY
    // - DD-MM-YYYY or D-M-YYYY
    // - (optionally) MM/DD/YYYY if you want, but UK Excel is usually DD/MM/YYYY
    function normalizeDateToIso(raw) {
        const s = String(raw ?? "").trim();
        if (!s) return "";

        // Already ISO?
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

        // DD/MM/YYYY or DD-MM-YYYY
        const m = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (m) {
            const dd = Number(m[1]);
            const mm = Number(m[2]);
            const yyyy = Number(m[3]);

            // basic sanity
            if (yyyy < 1900 || yyyy > 2100) return "";
            if (mm < 1 || mm > 12) return "";
            if (dd < 1 || dd > 31) return "";

            // Interpret as UK-style DD/MM/YYYY
            return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
        }

        return "";
    }

    function isIsoDate(v) {
        return /^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim());
    }

    const validateAndNormalizeCsvObjects = (objects) => {
        const errs = [];
        const out = [];

        // helpful for validation of bases/types
        const campusNames = new Set((campuses || []).map((c) => String(c.name || "").trim()));
        const productTypes = new Set(PRODUCT_TYPE_OPTIONS.map((t) => String(t)));

        objects.forEach((o, idx) => {
            const rowNum = idx + 2; // +1 header, +1 1-based rows

            // skip template/example row if present
            const sentinel = String(o[CSV_SENTINEL_HEADER] ?? "").trim().toLowerCase();
            if (sentinel && sentinel === CSV_SENTINEL_VALUE.toLowerCase()) {
                return;
            }

            const name = String(o.name ?? "").trim();
            const base = String(o.base ?? "").trim();
            const productType = canonicalProductType(o.productType);
            const residentialRaw = o.residential;
            const residentialFlag = asOptionalBool(residentialRaw);
            const startDateRaw = String(o.startDate ?? "").trim();
            const endDateRaw = String(o.endDate ?? "").trim();
            const startDate = normalizeDateToIso(startDateRaw);
            const endDate = normalizeDateToIso(endDateRaw);const defaultRateRaw = o.defaultRate;
            const capacityRaw = o.capacity;
            const commissionableRaw = o.commissionable;
            const notes = String(o.notes ?? "").trim();

            if (!name) errs.push(`Row ${rowNum}: name is required.`);
            if (!base) errs.push(`Row ${rowNum}: base is required.`);
            if (!productType) errs.push(`Row ${rowNum}: productType is required.`);
            if (startDateRaw && !startDate) errs.push(`Row ${rowNum}: startDate is not a recognized date (use YYYY-MM-DD or DD/MM/YYYY).`);
            if (endDateRaw && !endDate) errs.push(`Row ${rowNum}: endDate is not a recognized date (use YYYY-MM-DD or DD/MM/YYYY).`);

            if (productType && !productTypes.has(productType)) {
                // Name the types the way the screen does, so the fix is obvious.
                errs.push(
                `Row ${rowNum}: productType "${String(o.productType ?? "").trim()}" is not one of ${PRODUCT_TYPE_OPTIONS.map(displayProductType).join(", ")}.`
                );
            }

            // Blank is fine and means Yes; anything unrecognised is a typo we
            // must not read as No, since that silently changes what's created.
            if (residentialFlag === undefined) {
                errs.push(
                `Row ${rowNum}: residential "${String(residentialRaw).trim()}" must be Yes or No (or blank for Yes).`
                );
            }

            // base validation: MUST be a known campus/base
            const baseIsKnown =
                base &&
                campusNames.size > 0 &&
                campusNames.has(base); 
            if (!base) {
                errs.push(`Row ${rowNum}: base is required.`);
            } else if (campusNames.size > 0 && !campusNames.has(base)) {
                errs.push(
                    `Row ${rowNum}: base "${base}" is not one of your Bases. Create the Base first, then re-upload.`
                );
            }

            if (startDate && !isIsoDate(startDate)) errs.push(`Row ${rowNum}: startDate must be YYYY-MM-DD.`);
            if (endDate && !isIsoDate(endDate)) errs.push(`Row ${rowNum}: endDate must be YYYY-MM-DD.`);

            const defaultRate = asNumberOrNull(defaultRateRaw);
            if (defaultRateRaw !== undefined && defaultRateRaw !== null && String(defaultRateRaw).trim() !== "") {
                if (Number.isNaN(defaultRate)) errs.push(`Row ${rowNum}: defaultRate must be a number.`);
                if (!Number.isNaN(defaultRate) && defaultRate < 0) errs.push(`Row ${rowNum}: defaultRate cannot be negative.`);
            }

            const capacity = asNumberOrNull(capacityRaw);
            if (capacityRaw !== undefined && capacityRaw !== null && String(capacityRaw).trim() !== "") {
                if (Number.isNaN(capacity)) errs.push(`Row ${rowNum}: capacity must be a number.`);
                if (!Number.isNaN(capacity) && capacity < 0) errs.push(`Row ${rowNum}: capacity cannot be negative.`);
            }

            const commissionable = asBool(commissionableRaw);

            // if there are required-field errors, don’t push payload
            // (we’ll still list errors)
            const hasHardError =
                !name || !base || !productType || !startDate || !endDate || !baseIsKnown ||
                (defaultRateRaw && Number.isNaN(defaultRate)) ||
                (capacityRaw && Number.isNaN(capacity)) ||
                (startDate && !isIsoDate(startDate)) ||
                (endDate && !isIsoDate(endDate)) ||
                (productType && !productTypes.has(productType)) ||
                residentialFlag === undefined;

            if (hasHardError) return;

            out.push({
                name,
                base,
                productType,
                // Same rule the dialog saves by: the flag only means anything on
                // a BOOKING, everything else is residential. Blank defaults to
                // Yes, matching a freshly opened Add Product form.
                residential: isBookingType(productType) ? residentialFlag ?? true : true,
                startDate,
                endDate,
                defaultRate: defaultRate === null ? null : defaultRate,
                capacity: capacity === null ? null : Math.trunc(capacity),
                commissionable,
                notes,
            });
        });

        return { out, errs };
    };

    const handleBulkFile = async (file) => {
        if (!file) return;

        setBulkParsing(true);
        setBulkErrors([]);
        setBulkRows([]);
        setBulkFileName(file.name);

        try {
            const text = await file.text();
            const raw = parseCsv(text);

            if (!raw || raw.length < 1) {
                setBulkErrors(["CSV appears to be empty."]);
                return;
            }

            const headerRow = raw[0].map(normalizeHeader);

            // map header -> index
            const idx = {};
            headerRow.forEach((h, i) => { idx[h] = i; });

            // must include at least the key headers
            const requiredHeaders = ["name", "base", "producttype", "startdate", "enddate"];
            const missing = requiredHeaders.filter((h) => idx[h] === undefined);

            if (missing.length > 0) {
                setBulkErrors([`Missing required header(s): ${missing.join(", ")}.`]);
                return;
            }

            // Build objects for each row
            const objects = raw.slice(1).map((cells) => {
                const o = {};
                // read known columns (case-insensitive)
                o[CSV_SENTINEL_HEADER] = cells[idx[normalizeHeader(CSV_SENTINEL_HEADER)]] ?? "";

                o.name = cells[idx["name"]] ?? "";
                o.base = cells[idx["base"]] ?? "";
                o.productType = cells[idx["producttype"]] ?? "";
                o.startDate = cells[idx["startdate"]] ?? "";
                o.endDate = cells[idx["enddate"]] ?? "";

                // optional
                o.residential = idx["residential"] !== undefined ? (cells[idx["residential"]] ?? "") : "";
                o.defaultRate = idx["defaultrate"] !== undefined ? (cells[idx["defaultrate"]] ?? "") : "";
                o.capacity = idx["capacity"] !== undefined ? (cells[idx["capacity"]] ?? "") : "";
                o.commissionable = idx["commissionable"] !== undefined ? (cells[idx["commissionable"]] ?? "") : "";
                o.notes = idx["notes"] !== undefined ? (cells[idx["notes"]] ?? "") : "";

                return o;
            });

            const { out, errs } = validateAndNormalizeCsvObjects(objects);
            setBulkRows(out);
            setBulkErrors(errs);

            if (out.length === 0 && errs.length === 0) {
                setBulkErrors(["No importable rows found (did you only upload the template example row?)."]);
            }
        } catch (e) {
            console.error("Bulk CSV parse failed:", e);
            setBulkErrors(["Failed to read/parse the CSV file."]);
        } finally {
            setBulkParsing(false);
        }
    };

    const importBulkProducts = async () => {
        if (bulkRows.length === 0) {
            showToast("No valid rows to import.", "warning");
            return;
        }

        const token = sessionStorage.getItem("bearer");
        setBulkImporting(true);
        setBulkProgress({ done: 0, total: bulkRows.length });

        // Import sequentially (safer for auditing + avoids hammering backend)
        // If you later add a bulk endpoint, you can swap this out.
        let ok = 0;
        let fail = 0;

        for (let i = 0; i < bulkRows.length; i++) {
            const payload = bulkRows[i];

            try {
                const res = await fetch(`${SERVER_URL}api/products`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                if (res.ok) {
                    ok++;
                } else {
                    fail++;
                    const text = await res.text().catch(() => "");
                    console.warn("Bulk row failed:", payload?.name, res.status, text);
                }
            } catch (e) {
                fail++;
                console.warn("Bulk row network fail:", payload?.name, e);
            } finally {
                setBulkProgress({ done: i + 1, total: bulkRows.length });
            }
        }

        setBulkImporting(false);

        if (ok > 0) {
            showToast(`Imported ${ok} product(s).${fail ? ` ${fail} failed.` : ""}`, fail ? "warning" : "success");
            setBulkOpen(false);
            await fetchProducts();
        } else {
            showToast("No products were imported (all rows failed).", "error");
        }
    };

    const isSelected = (id) => selectedIds.has(id);

    const toggleSelected = (p) => {
        const id = p?.id;
        if (!id) return;

        // prevent selecting non-deletable rows
        if (!canDeleteProduct(p)) {
            showToast("This product has bookings and cannot be deleted.", "warning");
            return;
        }

        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const selectedProducts = useMemo(() => {
        const map = new Map((filtered || []).map((p) => [p.id, p]));
        return Array.from(selectedIds)
            .map((id) => map.get(id))
            .filter(Boolean);
    }, [selectedIds, filtered]);

    const selectedDeletable = useMemo(
        () => selectedProducts.filter((p) => canDeleteProduct(p)),
        [selectedProducts]
    );

    const selectedNotDeletable = useMemo(
        () => selectedProducts.filter((p) => !canDeleteProduct(p)),
        [selectedProducts]
    );

    // Header select-all should operate on currently filtered AND deletable rows
    const deletableFilteredIds = useMemo(() => {
        return (filtered || [])
            .filter((p) => canDeleteProduct(p))
            .map((p) => p.id);
    }, [filtered]);

    const allDeletableSelected =
        deletableFilteredIds.length > 0 &&
        deletableFilteredIds.every((id) => selectedIds.has(id));

    const someDeletableSelected =
        deletableFilteredIds.some((id) => selectedIds.has(id)) && !allDeletableSelected;

    const toggleSelectAllDeletable = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);

            if (allDeletableSelected) {
                // unselect all deletable in current view
                deletableFilteredIds.forEach((id) => next.delete(id));
            } else {
                // select all deletable in current view
                deletableFilteredIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    return (
        <>
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label="All" />
                        <Tab label="Booked" />
                        <Tab label="Sold out" />
                    </Tabs>

                    <Box sx={{ flexGrow: 1 }} />

                        <IconButton onClick={fetchProducts} title="Refresh">
                            <RefreshIcon />
                        </IconButton>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAdd}
                        >
                            Add Product
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={openBulk}
                        >
                            Bulk Upload
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            disabled={selectedIds.size === 0}
                            onClick={() => setBulkDeleteOpen(true)}
                        >
                            Delete Selected ({selectedIds.size})
                        </Button>

                    </Box>

                    <TabPanel value={tab} index={tab}>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                <TextField
                                    label="Filter name"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    size="small"
                                />
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="base-filter-label">Base</InputLabel>
                                    <Select
                                        labelId="base-filter-label"
                                        label="Base"
                                        value={baseFilter}
                                        onChange={(e) => setBaseFilter(e.target.value)}
                                    >
                                    <MenuItem value="">All</MenuItem>
                                        {campuses.map((c) => (
                                            <MenuItem key={c.id} value={c.name}>
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="product-type-filter-label">
                                        Product type
                                    </InputLabel>
                                    <Select
                                        labelId="product-type-filter-label"
                                        label="Product type"
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                    >
                                    <MenuItem value="">All</MenuItem>
                                        {PRODUCT_TYPE_OPTIONS.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {displayProductType(t)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    sx={rangeSwapped ? { bgcolor: "action.hover" } : undefined}
                                    label="Start date"
                                    type="date"
                                    value={rangeStart}
                                    onChange={(e) => setRangeStart(e.target.value)}
                                    onBlur={normalizeRange}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") normalizeRange();
                                    }}
                                />

                                <TextField
                                    sx={rangeSwapped ? { bgcolor: "action.hover" } : undefined}
                                    label="End date"
                                    type="date"
                                    value={rangeEnd}
                                    onChange={(e) => setRangeEnd(e.target.value)}
                                    onBlur={normalizeRange}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") normalizeRange();
                                    }}
                                />
                            </Box>
                            
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setNameFilter("");
                                    setBaseFilter("");
                                    setTypeFilter("");
                                    setRangeStart("");
                                    setRangeEnd("");
                                }}
                            >
                                Clear
                            </Button>

                        </Paper>

                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                <CircularProgress />
                            </Box>
                        )  : (
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={someDeletableSelected}
                                                    checked={allDeletableSelected}
                                                    onChange={toggleSelectAllDeletable}
                                                    inputProps={{ "aria-label": "select all deletable" }}
                                                />
                                            </TableCell>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Base</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Residential</TableCell>
                                            <TableCell>Start</TableCell>
                                            <TableCell>End</TableCell>
                                            <TableCell align="right">Rate</TableCell>
                                            <TableCell align="right">Cap.</TableCell>
                                            <TableCell align="right">Alloc.</TableCell>
                                            <TableCell>Comm.</TableCell>
                                            <TableCell>Notes</TableCell>
                                            <TableCell align="center">Edit</TableCell>
                                            <TableCell align="center">Delete</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filtered.map((p) => (
                                            <TableRow key={p.id} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected(p.id)}
                                                        disabled={!canDeleteProduct(p)}
                                                        onChange={() => toggleSelected(p)}
                                                    />
                                                </TableCell>
                                                <TableCell>{p.id}</TableCell>
                                                <TableCell>{p.name || "—"}</TableCell>
                                                <TableCell>{p.base || "—"}</TableCell>
                                                <TableCell>{displayProductType(p.productType)}</TableCell>
                                                <TableCell>
                                                    {String(p.productType || "").toUpperCase() === "RESIDENTIAL"
                                                        ? (p.residential ? "Yes" : "No")
                                                        : ""}
                                                </TableCell>
                                                <TableCell>{p.startDate || "—"}</TableCell>
                                                <TableCell>{p.endDate || "—"}</TableCell>
                                                <TableCell align="right">
                                                    {p.defaultRate ?? "—"}
                                                </TableCell>
                                                <TableCell align="right">{p.capacity ?? "—"}</TableCell>
                                                <TableCell align="right">{p.allocated ?? "—"}</TableCell>
                                                <TableCell>{p.commissionable ? "Yes" : "No"}</TableCell>
                                                <TableCell sx={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {p.notes || "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton onClick={() => openEdit(p)} size="small" title="Edit">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton onClick={() => openClone(p)} size="small" title="Duplicate as new product">
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton onClick={() => openDelete(p)} size="small">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {filtered.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                                                    No products match your filters.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </TabPanel>
                </Box>

                <AddEditProductDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    initial={editingProduct}
                    onSave={handleSave}
                    productTypeOptions={PRODUCT_TYPE_OPTIONS}
                    campuses={campuses}
                    campusSessions={campusSessions}
                />

                <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="sm" fullWidth>
                    <DialogTitle>Delete product?</DialogTitle>
                        <DialogContent dividers>
                            <Typography sx={{ mb: 1 }}>
                                You are about to delete:
                            </Typography>

                            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                <Typography><b>#{deletingProduct?.id}</b> {deletingProduct?.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {deletingProduct?.base} • {deletingProduct?.productType} • {deletingProduct?.startDate} → {deletingProduct?.endDate}
                                </Typography>
                            </Paper>

                            <Typography variant="body2">
                                This cannot be undone.
                                <br />
                                <b>Note:</b> If this product already has bookings, deletion will fail. In that case, cancel bookings first.
                            </Typography>
                        </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDelete} variant="outlined" disabled={deleteLoading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            color="error"
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={bulkOpen} onClose={closeBulk} maxWidth="md" fullWidth>
                    <DialogTitle>Bulk upload products (CSV)</DialogTitle>
                        <DialogContent dividers>
                            <Typography sx={{ mb: 2 }} variant="body2">
                                Download the template, fill it in, then upload it here. The included example rows are safe to leave in —
                                they will be detected and skipped using <b>{CSV_SENTINEL_HEADER}={CSV_SENTINEL_VALUE}</b>.
                            </Typography>

                            <Typography sx={{ mb: 2 }} variant="body2" color="text.secondary">
                                For a <b>BOOKING</b> row, set <b>residential</b> to Yes or No; leave it blank for Yes.
                                It doesn&apos;t apply to the other product types.
                            </Typography>

                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => {
                                        const content = buildTemplateCsv();
                                        downloadCsv(`Products_Template_${selectedYear}.csv`, content);
                                    }}
                                    disabled={bulkParsing || bulkImporting}
                                >
                                    Download template
                                </Button>

                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<UploadFileIcon />}
                                    disabled={bulkParsing || bulkImporting}
                                >
                                    Choose CSV…
                                    <input
                                        hidden
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            // allow re-uploading same file by clearing input
                                            e.target.value = "";
                                            handleBulkFile(f);
                                        }}
                                    />
                                </Button>

                                {bulkFileName && (
                                    <Typography variant="body2" sx={{ alignSelf: "center" }}>
                                        File: <b>{bulkFileName}</b>
                                    </Typography>
                                )}
                            </Box>

                            {bulkParsing ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Parsing CSV…</Typography>
                                 </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="body2">
                                                Rows ready to import: <b>{bulkRows.length}</b>
                                            </Typography>
                                        </Paper>

                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="body2">
                                                Messages: <b>{bulkErrors.length}</b>
                                            </Typography>
                                        </Paper>

                                        {bulkImporting && (
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                <Typography variant="body2">
                                                    Importing: <b>{bulkProgress.done}</b> / <b>{bulkProgress.total}</b>
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Box>

                                    {bulkErrors.length > 0 && (
                                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Validation / warnings
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                {bulkErrors.slice(0, 20).map((e, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2">{e}</Typography>
                                                    </li>
                                                ))}
                                                {bulkErrors.length > 20 && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            …and {bulkErrors.length - 20} more
                                                        </Typography>
                                                    </li>
                                                )}
                                            </Box>
                                        </Paper>
                                    )}

                                    {bulkRows.length > 0 && (
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Preview (first 5 rows)
                                            </Typography>

                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Base</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Resid.</TableCell>
                                                        <TableCell>Start</TableCell>
                                                        <TableCell>End</TableCell>
                                                        <TableCell align="right">Rate</TableCell>
                                                        <TableCell align="right">Cap.</TableCell>
                                                        <TableCell>Comm.</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {bulkRows.slice(0, 5).map((r, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{r.name}</TableCell>
                                                            <TableCell>{r.base}</TableCell>
                                                            <TableCell>{displayProductType(r.productType)}</TableCell>
                                                            <TableCell>
                                                                {isBookingType(r.productType) ? (r.residential ? "Yes" : "No") : ""}
                                                            </TableCell>
                                                            <TableCell>{r.startDate}</TableCell>
                                                            <TableCell>{r.endDate}</TableCell>
                                                            <TableCell align="right">
                                                                {r.defaultRate ?? "—"}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {r.capacity ?? "—"}
                                                            </TableCell>
                                                            <TableCell>{r.commissionable ? "Yes" : "No"}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    )}
                                </>
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={closeBulk} variant="outlined" disabled={bulkParsing || bulkImporting}>
                                Close
                            </Button>

                            <Button
                                onClick={importBulkProducts}
                                variant="contained"
                                disabled={bulkParsing || bulkImporting || bulkRows.length === 0}
                            >
                                {bulkImporting ? "Importing…" : `Import ${bulkRows.length} product(s)`}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleteLoading && setBulkDeleteOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Delete selected products?</DialogTitle>
                        <DialogContent dividers>
                            <Typography sx={{ mb: 1 }}>
                                You have selected <b>{selectedIds.size}</b> product(s).
                            </Typography>

                            {selectedNotDeletable.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        These cannot be deleted (they have bookings)
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {selectedNotDeletable.slice(0, 10).map((p) => (
                                            <li key={p.id}>
                                                <Typography variant="body2">
                                                    #{p.id} {p.name} (allocated: {p.allocated ?? "?"})
                                                </Typography>
                                            </li>
                                        ))}
                                        {selectedNotDeletable.length > 10 && (
                                            <li>
                                                <Typography variant="body2">
                                                    …and {selectedNotDeletable.length - 10} more
                                                </Typography>
                                            </li>
                                        )}
                                    </Box>
                                </Paper>
                            )}

                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Will delete
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>{selectedDeletable.length}</b> product(s) will be deleted.
                                </Typography>

                                {selectedDeletable.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No deletable items selected.
                                    </Typography>
                                ) : (
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {selectedDeletable.slice(0, 10).map((p) => (
                                            <li key={p.id}>
                                                <Typography variant="body2">
                                                    #{p.id} {p.name}
                                                </Typography>
                                            </li>
                                        ))}
                                        {selectedDeletable.length > 10 && (
                                            <li>
                                                <Typography variant="body2">
                                                    …and {selectedDeletable.length - 10} more
                                                </Typography>
                                            </li>
                                        )}
                                    </Box>
                                )}
                            </Paper>

                            <Typography variant="body2" sx={{ mt: 2 }}>
                                This cannot be undone.
                            </Typography>
                        </DialogContent>

                        <DialogActions>
                            <Button
                                onClick={() => { if (!bulkDeleteLoading) { setBulkDeleteOpen(false); } }}
                                variant="outlined"
                                disabled={bulkDeleteLoading}
                            >
                                Cancel
                            </Button>

                            <Button
                                onClick={async () => {
                                    if (selectedDeletable.length === 0) {
                                        showToast("No deletable products selected.", "warning");
                                        return;
                                    }

                                    const token = sessionStorage.getItem("bearer");
                                    setBulkDeleteLoading(true);

                                    let ok = 0;
                                    let fail = 0;

                                    try {
                                        for (const p of selectedDeletable) {
                                            try {
                                                const res = await fetch(`${SERVER_URL}api/products/${p.id}`, {
                                                    method: "DELETE",
                                                    headers: { Authorization: `Bearer ${token}` },
                                                });

                                                if (res.ok) ok++;
                                                else fail++;
                                            } catch (e) {
                                                fail++;
                                            }
                                        }

                                        if (ok > 0) showToast(`Deleted ${ok} product(s).${fail ? ` ${fail} failed.` : ""}`, fail ? "warning" : "success");
                                        else showToast("No products were deleted.", "error");

                                        clearSelection();
                                        setBulkDeleteOpen(false);
                                        await fetchProducts();
                                    } finally {
                                        setBulkDeleteLoading(false);
                                    }
                                }}
                                variant="contained"
                                color="error"
                                disabled={bulkDeleteLoading || selectedDeletable.length === 0}
                            >
                                {bulkDeleteLoading ? "Deleting..." : `Delete ${selectedDeletable.length}`}
                            </Button>
                        </DialogActions>
                    </Dialog>


                <Snackbar
                    open={toastOpen}
                    autoHideDuration={4000}
                    onClose={handleToastClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    >
                    <MuiAlert
                        onClose={handleToastClose}
                        severity={toastSeverity}
                        elevation={6}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toastMessage}
                    </MuiAlert>
                </Snackbar>
            </>
    );
}
